import type { CalculatorArticleContent } from "../calculator-article";

export const ffmiGuide: CalculatorArticleContent = {
  id: "ffmi",
  eyebrow: "Vücut kompozisyonu rehberi",
  updatedAt: "29 Eylül 2026",
  articleTitle: "FFMI değerini hesaplamak ve bağlamıyla yorumlamak",
  intro:
    "FFMI, tahmini yağsız vücut kütleni boyuna göre ölçekler. BMI toplam vücut ağırlığını kullanırken FFMI yağ kütlesini ayırmaya çalışır. Yine de FFMI doğrudan kas ölçümü değildir; kullandığın yağ oranı tahminindeki hata, hidrasyon ve ölçüm koşulları sonuca taşınır.",
  sections: [
    {
      id: "ffmi-nedir",
      title: "FFMI nedir ve nasıl hesaplanır?",
      paragraphs: [
        "Önce yağsız kütle hesaplanır: vücut ağırlığı × (1 − yağ oranı). Ardından bu değer metre cinsinden boyun karesine bölünür. Örneğin 80 kg ve yüzde 15 yağ oranına sahip birinin tahmini yağsız kütlesi 68 kg'dır. Boyu 1,80 metre ise FFMI yaklaşık 21 olur.",
        "Yağsız kütle yalnız kas değildir. Su, kemik, organlar, bağ dokusu ve diğer yağ dışı dokular da bu değerin içindedir. Bu nedenle FFMI sonucunu 'kas kütlem' diye okumak hatalıdır. Daha doğru ifade, boya göre ölçeklenmiş tahmini yağsız kütledir.",
        "Normalize FFMI, farklı boylardaki kişileri karşılaştırırken boy etkisini azaltmak amacıyla 1,80 metre referansına düzeltme uygular. Kouri ve arkadaşlarının çalışmasında kullanılan formül, ham FFMI değerine 6,1 × (1,80 − boy) ekler. Bu düzeltme de bir modeldir; kişisel potansiyelin kesin ölçüsü değildir.",
      ],
      sourceIds: ["1"],
    },
    {
      id: "veri-kalitesi",
      title: "Yağ oranı tahmini sonucu nasıl değiştirir?",
      paragraphs: [
        "FFMI hesaplamasının en hassas girdisi yağ oranıdır. Gerçek yağ oranından daha düşük bir yüzde girersen yağsız kütle ve FFMI olduğundan yüksek çıkar; daha yüksek bir yüzde girersen tersi olur. Akıllı tartı, mezura, kaliper ve görüntüleme yöntemleri aynı kişide farklı sonuç üretebilir.",
        "Gelişimi takip ederken yöntemler arasında geçiş yapmak, gerçek değişim olmadan FFMI sıçraması yaratabilir. Aynı cihazı veya aynı çevre ölçümü protokolünü, benzer saat ve hidrasyon koşullarını kullan. Tek bir ondalık farka anlam yüklemek yerine aylar içindeki eğilimi değerlendir.",
        "Vücut ağırlığı da kısa vadede su ve glikojen nedeniyle değişir. Karbonhidrat alımı, sodyum, antrenman hasarı ve bağırsak içeriği birkaç gün içinde yağsız kütle tahminini oynatabilir. Bu yüzden FFMI günlük takip metriği olmaktan çok dönemsel karşılaştırma aracıdır.",
      ],
      table: {
        caption: "FFMI takibinde ölçüm tutarlılığı",
        headers: ["Girdi", "Sapma kaynağı", "Daha tutarlı yaklaşım"],
        rows: [
          [
            "Vücut ağırlığı",
            "Su, glikojen, yemek ve sodyum",
            "Benzer saat ve koşulda birkaç ölçümün ortalaması",
          ],
          [
            "Yağ oranı",
            "Yöntem ve uygulayıcı farkı",
            "Aynı yöntemi ve aynı protokolü sürdürmek",
          ],
          [
            "Boy",
            "Ölçüm tekniği ve gün içi küçük fark",
            "Ayakkabısız, dik ve güvenilir ölçüm",
          ],
          [
            "Karşılaştırma",
            "Farklı cihaz veya denklem",
            "Ham veriyi ve kullanılan yöntemi kaydetmek",
          ],
        ],
      },
      sourceIds: ["2"],
    },
    {
      id: "ffmi-yorumlama",
      title: "FFMI aralıklarını kesin sınır gibi görme",
      paragraphs: [
        "İnternette FFMI için sık kullanılan sınıflandırmalar vardır. Bunların çoğu belirli örneklemlerden üretilmiş pratik yorumlardır; evrensel tanı eşiği değildir. Cinsiyet, yaş, etnik köken, spor geçmişi, iskelet yapısı ve ölçüm yöntemi sonucu etkiler.",
        "Kouri ve arkadaşlarının çalışması, steroid kullandığını bildiren ve bildirmeyen erkek sporculardaki yağsız kütleyi karşılaştırdı. Çalışmadan türeyen yaklaşık 25 normalize FFMI değeri internette çoğu zaman 'doğal limit' diye sunulur. Oysa bu, herkes için biyolojik tavanı kanıtlayan bir eşik değildir; örnekleme ve ölçüm yöntemine bağlı araştırma bulgusudur.",
        "Bir değerin yüksek olması performansın, sağlığın veya kas kalitesinin otomatik göstergesi değildir. Kuvvet teknik, sinir sistemi uyumu, kaldıraçlar ve antrenman deneyiminden de etkilenir. FFMI'yi kimlik veya yeterlilik puanı yerine vücut kompozisyonu eğilimini anlamaya yarayan yardımcı metrik olarak kullan.",
      ],
      sourceIds: ["1", "2"],
    },
    {
      id: "pratik-kullanim",
      title: "FFMI gelişim takibinde nasıl kullanılır?",
      paragraphs: [
        "Kas kazanımı hedefinde yalnız tartı artışına bakmak, yağ ve yağsız kütle değişimini ayırmaz. FFMI tahmini bu ayrımı kusursuz yapmasa da aynı yöntemle ölçüldüğünde ek bağlam sağlar. Vücut ağırlığın artarken yağ oranı benzer kalıyor ve antrenman performansın ilerliyorsa yağsız kütle eğilimi olumlu olabilir.",
        "Yağ kaybı döneminde vücut ağırlığı azalırken FFMI'nin büyük ölçüde korunması, tahmini yağsız kütlenin korunduğuna işaret edebilir. Ancak su ve glikojen azalması FFMI'yi kısa süreli düşürebilir. Sonucu bel çevresi, kuvvet performansı, fotoğraflar ve haftalık ağırlık ortalamasıyla birlikte yorumla.",
      ],
      steps: [
        "Vücut ağırlığını ve boyunu doğru birimle gir.",
        "Yağ oranını mümkün olduğunca tutarlı bir yöntemden al.",
        "Ham ve normalize FFMI sonuçlarını kaydet; yöntemi de not et.",
        "Karşılaştırmayı günlük değil, dört-sekiz haftalık dönemlerde yap.",
        "Sonucu kuvvet, çevre ölçümleri ve ağırlık eğilimiyle birlikte değerlendir.",
      ],
    },
    {
      id: "ffmi-bmi-farki",
      title: "FFMI, BMI ve yağ oranı aynı şeyi söylemez",
      paragraphs: [
        "BMI toplam vücut ağırlığını boyun karesine böler ve yağ ile yağsız kütleyi ayırmaz. Kaslı bir sporcu yüksek BMI değerine sahip olabilir. FFMI ise yağ oranı girdisiyle yağsız kütleyi ayırmaya çalıştığı için kuvvet sporlarında daha fazla bağlam sunar; fakat ölçüm hatasına daha açıktır.",
        "Yağ oranı, toplam ağırlığın tahmini ne kadarının yağ olduğunu söyler. FFMI ise tahmini yağsız kısmı boya göre ölçekler. Aynı yağ oranındaki iki kişinin boyu ve ağırlığı farklı olduğunda FFMI değerleri farklı olabilir. Bu metriklerden hiçbiri tek başına sağlık değerlendirmesi değildir.",
        "Hangi metriğin önemli olduğu soruya bağlıdır. Genel nüfus taramalarında BMI pratik olabilir; vücut kompozisyonu takibinde yağ oranı ve FFMI ek bilgi verebilir. Kişisel karar için ölçüm kalitesi, sağlık geçmişi ve uzman değerlendirmesi gerekir.",
      ],
      sourceIds: ["2"],
    },
    {
      id: "ffmi-hatalar",
      title: "Sık yapılan hatalar",
      paragraphs: [
        "FFMI basit formülü nedeniyle olduğundan kesin algılanabilir. Aşağıdaki hatalar sonucu yanlış yorumlamaya yol açar.",
      ],
      bullets: [
        "Yağsız kütleyi yalnız iskelet kası sanmak.",
        "Akıllı tartı ve kaliper sonucunu aynı seri içinde doğrudan karşılaştırmak.",
        "Bir günlük su değişimini kas kazanımı veya kaybı kabul etmek.",
        "Normalize FFMI 25 değerini herkes için kesin doğal sınır saymak.",
        "Kadın ve erkek sonuçlarını aynı referans aralığıyla yorumlamak.",
        "FFMI yükseldi diye kuvvet veya sağlık durumunun otomatik iyileştiğini varsaymak.",
        "Yağ oranı girdisinin belirsizliğini raporlamamak.",
      ],
      note: "FFMI bir tanı aracı değildir. Hızlı ve açıklanamayan kilo değişimi, ödem veya sağlık kaygısı varsa hesaplayıcı sonucu yerine sağlık profesyonelinin değerlendirmesini esas al.",
    },
    {
      id: "uygulama-ornegi",
      title: "Uygulama örneği: FFMI değişimini doğru okumak",
      paragraphs: [
        "Başlangıçta 80 kg, 1,80 metre ve yüzde 15 tahmini yağ oranına sahip olduğunu düşün. Yağsız kütle yaklaşık 68 kg, ham FFMI yaklaşık 21 olur. Sekiz hafta sonra ağırlığın 82 kg ve yağ oranı tahminin yüzde 16 ise yağsız kütle yaklaşık 68,9 kg görünür. Kâğıt üzerinde artış vardır; fakat yağ oranındaki bir puanlık ölçüm hatası bu farkın önemli kısmını açıklayabilir.",
        "Bu dönemde aynı cihazı, benzer sabah koşullarını ve aynı protokolü kullandıysan; bel çevresi sınırlı artmış, kol veya bacak çevresi ile performans ilerlemişse yağsız kütle artışı yorumu güçlenir. Yağ oranını ilk ölçümde Navy yöntemiyle, ikinci ölçümde akıllı tartıyla aldıysan iki FFMI sonucunu doğrudan kıyaslamak daha zayıf bir çıkarımdır.",
        "Yağ kaybı döneminde 78 kg'a düşüp tahmini yağ oranını yüzde 12 ölçtüğünde hesaplanan yağsız kütle önceki değere yakın kalabilir. Bu, planın yağsız kütleyi koruduğuna dair yararlı işarettir; ancak kesin kas koruması kanıtı değildir. Kuvvet trendi, çevre ölçümleri ve antrenman kalitesi aynı yöndeyse yorum daha güvenilir olur.",
        "Pratik raporda yalnız FFMI ondalığını değil; tarih, kilo, yağ oranı, yöntem ve temel performans göstergelerini birlikte sakla. Böylece sonraki değişimin gerçek dokudan mı, ölçüm yönteminden mi geldiğini ayırman kolaylaşır.",
      ],
    },
    {
      id: "olcum-yontemleri",
      title: "Yağ oranı yöntemleri FFMI'yi neden farklı gösterir?",
      paragraphs: [
        "Çevre ölçümüne dayalı Navy yöntemi, vücuttaki belirli çevrelerin boyla ilişkisini kullanır. Kaliper deri kıvrımı kalınlığından vücut yoğunluğu tahmini üretir. BIA cihazları elektrik akımına verilen tepkiyi, DEXA ise X-ışını zayıflamasını kullanır. Hepsi farklı varsayım ve hata kaynaklarına sahiptir.",
        "Daha gelişmiş görünen cihaz da her koşulda hatasız değildir. BIA sonucu hidrasyon, yemek, egzersiz ve cihaz algoritmasından etkilenebilir. DEXA cihazı, yazılım ve ölçüm protokolü arasında fark olabilir. Kaliper uygulayıcının nokta seçimi ve becerisine duyarlıdır. FFMI bu yöntemlerin ürettiği yağ oranını gerçek kabul ederek hesaplandığı için belirsizliği devralır.",
        "Bu nedenle amaç yöntemleri karıştırarak en hoşuna giden sonucu seçmek değil, izlemede tutarlılık kurmaktır. Bir yöntemden diğerine geçmen gerekiyorsa yeni yöntemi yeni başlangıç çizgisi kabul et. Mümkünse geçiş gününde iki yöntemi de uygulayıp aralarındaki sistematik farkı kaydet.",
      ],
      subsections: [
        {
          title: "Ölçüm hatasını rapora nasıl yansıtabilirsin?",
          paragraphs: [
            "Raporunda yalnız 'FFMI 22,1' yazmak yerine 'Navy çevre ölçümüyle tahmini FFMI 22,1' demek daha şeffaftır. Boy, kilo, yağ oranı, ölçüm tarihi ve yöntem saklandığında hesap sonradan yeniden üretilebilir. Bu yaklaşım tek ondalığın sahte kesinliğini azaltır.",
            "Küçük değişimin olası ölçüm hatası aralığında olabileceğini kabul et. Güç artışı, çevre değişimi ve birkaç dönem boyunca aynı yönde FFMI eğilimi birlikte görülüyorsa yağsız kütle gelişimi yorumu daha sağlam hâle gelir.",
          ],
        },
        {
          title: "Kadın sporcularda yorum",
          paragraphs: [
            "Kadın ve erkeklerin vücut kompozisyonu dağılımı ve araştırma referansları aynı değildir. Erkek sporcu örnekleminden gelen popüler FFMI eşiklerini kadınlara uygulamak doğru olmaz. Ölçüm eğilimini kişinin kendi başlangıç değeri ve hedefleri üzerinden değerlendirmek daha kullanışlıdır.",
          ],
        },
      ],
      sourceIds: ["1", "2"],
    },
  ],
  faqs: [
    {
      question: "FFMI kas kütlesini doğrudan ölçer mi?",
      answer:
        "Hayır. Yağsız vücut kütlesini boya göre ölçekler; yağsız kütle kasla birlikte su, kemik, organ ve diğer yağ dışı dokuları içerir.",
    },
    {
      question: "Normalize FFMI neden farklı çıkar?",
      answer:
        "Farklı boyların karşılaştırılmasını kolaylaştırmak için ham FFMI 1,80 metre referansına göre düzeltilir. Bu da tahmini bir modeldir.",
    },
    {
      question: "FFMI 25 doğal sınır mıdır?",
      answer:
        "Hayır. Yaklaşık 25 değeri belirli bir erkek sporcu örneklemindeki araştırmadan yaygınlaşmıştır; herkes için kanıtlanmış biyolojik tavan değildir.",
    },
    {
      question: "Hangi yağ oranı değerini girmeliyim?",
      answer:
        "Mümkünse düzenli olarak kullandığın aynı yöntemden gelen değeri gir. Yöntem değiştirirsen FFMI değişiminin bir kısmı gerçek doku değişimi değil ölçüm farkı olabilir.",
    },
    {
      question: "FFMI ne sıklıkla hesaplanmalı?",
      answer:
        "Günlük hesaplamak anlamlı değildir. Benzer koşullarda dört-sekiz haftalık dönemlerle bakmak kısa vadeli su değişiminin etkisini azaltır.",
    },
    {
      question: "Kilo verirken FFMI neden düşebilir?",
      answer:
        "Gerçek yağsız doku kaybının yanında su ve glikojen azalması veya yağ oranı ölçüm farkı da sonucu düşürebilir. Performans ve uzun dönem eğilimiyle birlikte değerlendir.",
    },
    {
      question: "Yüksek FFMI daha güçlü olduğum anlamına gelir mi?",
      answer:
        "Tek başına hayır. Kuvvet teknik, kaldıraç, sinir sistemi uyumu ve antrenman geçmişinden etkilenir.",
    },
    {
      question: "FFMI sağlık göstergesi midir?",
      answer:
        "Tek başına değildir. Bir vücut kompozisyonu tahminidir; sağlık değerlendirmesi için klinik bağlam ve uygun uzman gerekir.",
    },
    {
      question: "Akıllı tartı yağ oranıyla FFMI hesaplanır mı?",
      answer:
        "Hesaplanabilir; ancak BIA sonucu hidrasyon, yemek ve cihaz algoritmasından etkilenir. Takipte aynı cihazı benzer koşullarda kullan ve çıkan FFMI'yi doğrudan kas ölçümü gibi sunma.",
    },
    {
      question: "FFMI artarken bel çevrem de artıyorsa ne anlama gelir?",
      answer:
        "Yağsız kütleyle birlikte yağ kütlesi de artmış olabilir veya yağ oranı tahmini hatalı olabilir. Bel çevresi, ağırlık hızı, kuvvet ve aynı yöntemle alınmış birkaç ölçümü birlikte değerlendir.",
    },
    {
      question: "Boy uzadıkça normalize FFMI neden değişir?",
      answer:
        "Ham FFMI boyun karesini kullansa da uzunluklar arasında kalan sistematik farkı azaltmak için 1,80 metre referansına ek düzeltme yapılır. Bu düzeltme kişisel biyolojik potansiyeli ölçmez.",
    },
  ],
  sources: [
    {
      id: "1",
      label: "Kouri ve arkadaşları — erkek sporcularda FFMI ve yağsız kütle",
      href: "https://pubmed.ncbi.nlm.nih.gov/7496846/",
    },
    {
      id: "2",
      label: "CDC — BMI'nin kullanımı ve vücut kompozisyonu sınırlılıkları",
      href: "https://www.cdc.gov/bmi/about/index.html",
    },
  ],
  related: [
    {
      href: "/essay/body-fat-calculator",
      label: "Yağ oranını hesapla",
      description: "FFMI için kullanacağın tahmini yağ oranını bul.",
    },
    {
      href: "/essay/calori-calculator",
      label: "Kalori ihtiyacını hesapla",
      description: "Enerji hedefini ölçüm eğiliminle birlikte planla.",
    },
  ],
  primaryCta: {
    href: "/coachs",
    label: "Koçluk seçeneklerini incele",
    description:
      "Vücut kompozisyonu hedefini antrenman ve beslenme planına dönüştür.",
  },
};
